import { Locale } from "@/i18n-config";
import { defineLocaleRecord } from "@/i18n-config";

export type AppCopy = {
  auth: {
    login: {
      title: string;
      subtitle: string;
      emailLabel: string;
      passwordLabel: string;
      emailPlaceholder: string;
      passwordPlaceholder: string;
      submit: string;
      submitting: string;
      noAccount: string;
      createAccount: string;
      forgotPassword: string;
      loadingForm: string;
    };
    signup: {
      title: string;
      subtitle: string;
      emailLabel: string;
      passwordLabel: string;
      emailPlaceholder: string;
      passwordPlaceholder: string;
      submit: string;
      submitting: string;
      hasAccount: string;
      login: string;
      terms: string;
      successTitle: string;
      successIntro: string;
      successAction: string;
    };
    forgotPassword: {
      title: string;
      subtitle: string;
      emailLabel: string;
      emailPlaceholder: string;
      submit: string;
      submitting: string;
      successTitle: string;
      successMessage: string;
      backToLogin: string;
      invalidEmail: string;
      genericError: string;
    };
    resetPassword: {
      title: string;
      subtitle: string;
      passwordLabel: string;
      confirmPasswordLabel: string;
      passwordPlaceholder: string;
      confirmPasswordPlaceholder: string;
      submit: string;
      submitting: string;
      minLengthError: string;
      mismatchError: string;
      genericError: string;
      missingSessionTitle: string;
      missingSessionMessage: string;
      successTitle: string;
      successMessage: string;
      successAction: string;
      backToLogin: string;
      checkingSession: string;
    };
  };
  coursesPage: {
    title: string;
    subtitle: string;
    empty: string;
  };
  courseCard: {
    free: string;
    pro: string;
    openCourse: string;
  };
  courseDetail: {
    back: string;
    curriculum: string;
    noLessons: string;
    info: string;
    lessons: string;
    access: string;
    free: string;
    premium: string;
    start: string;
    soon: string;
  };
  dashboard: {
    title: string;
    welcome: string;
    completion: string;
    lessons: string;
    objectiveLabel: string;
    objectiveTitle: string;
    objectiveStart: string;
    objectiveEnd: string;
    continue: string;
    streakTitle: string;
    streakText: string;
    recommended: string;
    viewAll: string;
  };
  lessonPage: {
    back: string;
    label: string;
  };
  lessonContent: {
    markStep: string;
    contentSoon: string;
    previousLesson: string;
    takeQuiz: string;
    saving: string;
    completed: string;
    completeLesson: string;
    rewardTitle: string;
    rewardDescription: string;
    rewardButton: string;
    stepLabels: {
      watch: string;
      try: string;
      think: string;
      quickInsight: string;
      quickCheck: string;
      example: string;
      deepDive: string;
      teacherQuestion: string;
    };
    stepOf: string;
    justGettingStarted: string;
    scrollToContinue: string;
    keepScrollingToUnlock: string;
    phaseAnchors: {
      default: string;
      apply: string;
      pause: string;
      practical: string;
      guess: string;
    };
    almostThere: string;
    oneMoreBeforeQuickCheck: string;
    quickCheckNext: string;
    quickCheckIntro: string;
    teacherQuestionPrompt: string;
    deepDiveClosed: string;
    deepDiveOpen: string;
    nextUp: string;
  };
  quizPage: {
    subtitle: string;
  };
  quizClient: {
    completedTitle: string;
    scoredPrefix: string;
    backToCourses: string;
    retake: string;
    rewardTitle: string;
    rewardDescription: string;
    rewardButton: string;
    insight: string;
    lockIn: string;
    next: string;
    reveal: string;
  };
};

export const appCopyByLocale = defineLocaleRecord<AppCopy>({
  en: {
    auth: {
      login: {
        title: "Welcome back",
        subtitle: "Enter your details to continue.",
        emailLabel: "Email",
        passwordLabel: "Password",
        emailPlaceholder: "you@company.com",
        passwordPlaceholder: "••••••••",
        submit: "Log in",
        submitting: "Signing in...",
        noAccount: "Don't have an account?",
        createAccount: "Create account",
        forgotPassword: "Forgot password?",
        loadingForm: "Loading form...",
      },
      signup: {
        title: "Create your account",
        subtitle: "Start with one clear next step.",
        emailLabel: "Email",
        passwordLabel: "Password",
        emailPlaceholder: "you@company.com",
        passwordPlaceholder: "••••••••",
        submit: "Create account",
        submitting: "Creating account...",
        hasAccount: "Already have an account?",
        login: "Log in",
        terms: "By joining, you agree to our terms.",
        successTitle: "Check your email",
        successIntro: "We sent a confirmation link to",
        successAction: "Back to login",
      },
      forgotPassword: {
        title: "Reset password",
        subtitle: "Enter your email. If it exists, you will get a link.",
        emailLabel: "Email",
        emailPlaceholder: "you@company.com",
        submit: "Send reset email",
        submitting: "Sending...",
        successTitle: "Check your email",
        successMessage: "If the email exists, you will get a link.",
        backToLogin: "Back to login",
        invalidEmail: "Enter a valid email.",
        genericError: "Could not send the reset email. Try again.",
      },
      resetPassword: {
        title: "Set new password",
        subtitle: "Choose a new password for your account.",
        passwordLabel: "New password",
        confirmPasswordLabel: "Confirm new password",
        passwordPlaceholder: "At least 8 characters",
        confirmPasswordPlaceholder: "Repeat new password",
        submit: "Set new password",
        submitting: "Saving...",
        minLengthError: "Use at least 8 characters.",
        mismatchError: "Passwords do not match.",
        genericError: "Could not update the password. Try again.",
        missingSessionTitle: "Open the reset link from your email",
        missingSessionMessage: "For safety, this page works only from the email link.",
        successTitle: "Password updated",
        successMessage: "Your password was changed.",
        successAction: "Go to login",
        backToLogin: "Back to login",
        checkingSession: "Checking your reset link...",
      },
    },
    coursesPage: {
      title: "Available solutions",
      subtitle:
        "Pick one blocker and remove it. Start with the one slowing you down right now.",
      empty: "No courses yet. Check back soon.",
    },
    courseCard: {
      free: "FREE",
      pro: "PAID",
      openCourse: "Open solution",
    },
    courseDetail: {
      back: "Back to courses",
      curriculum: "Steps",
      noLessons: "No steps available yet.",
      info: "Course info",
      lessons: "Lessons",
      access: "Access",
      free: "Free",
      premium: "Premium",
      start: "Start now",
      soon: "Coming soon",
    },
    dashboard: {
      title: "Your progress",
      welcome: "Welcome back",
      completion: "Completion",
      lessons: "Lessons",
      objectiveLabel: "Current focus",
      objectiveTitle: "Keep moving",
      objectiveStart: "You have completed",
      objectiveEnd: "lessons. Keep going with one next step.",
      continue: "Continue",
      streakTitle: "Day streak",
      streakText: "You are making progress.",
      recommended: "Recommended next",
      viewAll: "View all",
    },
    lessonPage: {
      back: "Back to courses",
      label: "Lesson",
    },
    lessonContent: {
      markStep: "Continue",
      contentSoon: "Content coming soon.",
      previousLesson: "Previous lesson",
      takeQuiz: "Quick check",
      saving: "Saving...",
      completed: "Completed",
      completeLesson: "Finish lesson",
      rewardTitle: "Lesson complete",
      rewardDescription: "Next step is ready.",
      rewardButton: "Continue",
      stepLabels: {
        watch: "Watch",
        try: "Try",
        think: "Think",
        quickInsight: "Quick insight",
        quickCheck: "Quick check",
        example: "Example",
        deepDive: "Deep dive",
        teacherQuestion: "Question",
      },
      stepOf: "Step {current} of {total}",
      justGettingStarted: "0% · just getting started",
      scrollToContinue: "Scroll to continue",
      keepScrollingToUnlock: "Keep scrolling to unlock the next part",
      phaseAnchors: {
        default: "Now let's continue",
        apply: "Now let's apply this",
        pause: "Pause for a moment",
        practical: "Here's where it gets practical",
        guess: "Before I explain, take a guess",
      },
      almostThere: "Almost there.",
      oneMoreBeforeQuickCheck: "One more step before a quick check.",
      quickCheckNext: "Quick check is next.",
      quickCheckIntro: "Let's see if this clicked.",
      teacherQuestionPrompt: "What do you think?",
      deepDiveClosed: "Want to go deeper?",
      deepDiveOpen: "Hide details",
      nextUp: "Next up",
    },
    quizPage: {
      subtitle: "Quick check.",
    },
    quizClient: {
      completedTitle: "Quiz completed",
      scoredPrefix: "You got",
      backToCourses: "Back to courses",
      retake: "Retake",
      rewardTitle: "Great result",
      rewardDescription: "You are moving forward.",
      rewardButton: "Continue",
      insight: "Key idea",
      lockIn: "Check answer",
      next: "Next question",
      reveal: "Finish",
    },
  },
  fr: {
    auth: {
      login: {
        title: "Bon retour",
        subtitle: "Entrez vos informations pour continuer.",
        emailLabel: "Email",
        passwordLabel: "Mot de passe",
        emailPlaceholder: "vous@entreprise.com",
        passwordPlaceholder: "••••••••",
        submit: "Se connecter",
        submitting: "Connexion...",
        noAccount: "Pas de compte ?",
        createAccount: "Creer un compte",
        forgotPassword: "Mot de passe oublie ?",
        loadingForm: "Chargement du formulaire...",
      },
      signup: {
        title: "Creer votre compte",
        subtitle: "Commencez avec une prochaine etape claire.",
        emailLabel: "Email",
        passwordLabel: "Mot de passe",
        emailPlaceholder: "vous@entreprise.com",
        passwordPlaceholder: "••••••••",
        submit: "Creer le compte",
        submitting: "Creation du compte...",
        hasAccount: "Vous avez deja un compte ?",
        login: "Se connecter",
        terms: "En vous inscrivant, vous acceptez nos conditions.",
        successTitle: "Verifiez votre email",
        successIntro: "Nous avons envoye un lien de confirmation a",
        successAction: "Retour connexion",
      },
      forgotPassword: {
        title: "Reinitialiser le mot de passe",
        subtitle: "Entrez votre email. S'il existe, vous recevrez un lien.",
        emailLabel: "Email",
        emailPlaceholder: "vous@entreprise.com",
        submit: "Envoyer l'email de reinitialisation",
        submitting: "Envoi...",
        successTitle: "Verifiez votre email",
        successMessage: "Si l'email existe, vous recevrez un lien.",
        backToLogin: "Retour connexion",
        invalidEmail: "Entrez un email valide.",
        genericError: "Impossible d'envoyer l'email. Reessayez.",
      },
      resetPassword: {
        title: "Definir un nouveau mot de passe",
        subtitle: "Choisissez un nouveau mot de passe.",
        passwordLabel: "Nouveau mot de passe",
        confirmPasswordLabel: "Confirmez le nouveau mot de passe",
        passwordPlaceholder: "Au moins 8 caracteres",
        confirmPasswordPlaceholder: "Repetez le nouveau mot de passe",
        submit: "Definir le nouveau mot de passe",
        submitting: "Enregistrement...",
        minLengthError: "Utilisez au moins 8 caracteres.",
        mismatchError: "Les mots de passe ne correspondent pas.",
        genericError: "Impossible de mettre a jour le mot de passe. Reessayez.",
        missingSessionTitle: "Ouvrez le lien recu par email",
        missingSessionMessage: "Pour votre securite, cette page fonctionne uniquement via ce lien.",
        successTitle: "Mot de passe mis a jour",
        successMessage: "Votre mot de passe a ete modifie.",
        successAction: "Aller a la connexion",
        backToLogin: "Retour connexion",
        checkingSession: "Verification du lien...",
      },
    },
    coursesPage: {
      title: "Solutions disponibles",
      subtitle:
        "Choisissez un blocage et retirez-le. Commencez par celui qui vous ralentit maintenant.",
      empty: "Aucun parcours pour le moment. Revenez bientot.",
    },
    courseCard: {
      free: "GRATUIT",
      pro: "PAYANT",
      openCourse: "Ouvrir la solution",
    },
    courseDetail: {
      back: "Retour aux parcours",
      curriculum: "Etapes",
      noLessons: "Aucune etape disponible pour le moment.",
      info: "Infos du parcours",
      lessons: "Lecons",
      access: "Acces",
      free: "Gratuit",
      premium: "Premium",
      start: "Commencer",
      soon: "Bientot disponible",
    },
    dashboard: {
      title: "Votre progression",
      welcome: "Bon retour",
      completion: "Progression",
      lessons: "Lecons",
      objectiveLabel: "Priorite actuelle",
      objectiveTitle: "Continuez d'avancer",
      objectiveStart: "Vous avez termine",
      objectiveEnd: "lecons. Continuez avec une prochaine etape.",
      continue: "Continuer",
      streakTitle: "Serie de jours",
      streakText: "Vous avancez regulierement.",
      recommended: "Prochaines recommandations",
      viewAll: "Tout voir",
    },
    lessonPage: {
      back: "Retour aux parcours",
      label: "Lecon",
    },
    lessonContent: {
      markStep: "Continuer",
      contentSoon: "Contenu bientot disponible.",
      previousLesson: "Lecon precedente",
      takeQuiz: "Verification rapide",
      saving: "Enregistrement...",
      completed: "Termine",
      completeLesson: "Terminer la lecon",
      rewardTitle: "Lecon terminee",
      rewardDescription: "La prochaine etape est prete.",
      rewardButton: "Continuer",
      stepLabels: {
        watch: "Regarder",
        try: "Essayer",
        think: "Reflechir",
        quickInsight: "Info rapide",
        quickCheck: "Verification rapide",
        example: "Exemple",
        deepDive: "Approfondir",
        teacherQuestion: "Question",
      },
      stepOf: "Etape {current} sur {total}",
      justGettingStarted: "0 % · tout juste commence",
      scrollToContinue: "Faites defiler pour continuer",
      keepScrollingToUnlock: "Continuez a defiler pour debloquer la suite",
      phaseAnchors: {
        default: "On continue",
        apply: "Maintenant, appliquons cela",
        pause: "Pause un instant",
        practical: "Voici le cote pratique",
        guess: "Avant d'expliquer, devinez",
      },
      almostThere: "Presque fini.",
      oneMoreBeforeQuickCheck: "Encore une etape avant une verification rapide.",
      quickCheckNext: "Verification rapide ensuite.",
      quickCheckIntro: "Voyons si c'est clair.",
      teacherQuestionPrompt: "Qu'en pensez-vous ?",
      deepDiveClosed: "Envie d'aller plus loin ?",
      deepDiveOpen: "Masquer les details",
      nextUp: "Ensuite",
    },
    quizPage: {
      subtitle: "Verification rapide.",
    },
    quizClient: {
      completedTitle: "Quiz termine",
      scoredPrefix: "Vous avez obtenu",
      backToCourses: "Retour aux parcours",
      retake: "Recommencer",
      rewardTitle: "Excellent resultat",
      rewardDescription: "Vous avancez bien.",
      rewardButton: "Continuer",
      insight: "Idee cle",
      lockIn: "Verifier la reponse",
      next: "Question suivante",
      reveal: "Terminer",
    },
  },
  ru: {
    auth: {
      login: {
        title: "С возвращением",
        subtitle: "Введите данные, чтобы продолжить.",
        emailLabel: "Email",
        passwordLabel: "Пароль",
        emailPlaceholder: "you@company.com",
        passwordPlaceholder: "••••••••",
        submit: "Войти",
        submitting: "Вход...",
        noAccount: "Нет аккаунта?",
        createAccount: "Создать аккаунт",
        forgotPassword: "Забыли пароль?",
        loadingForm: "Загрузка формы...",
      },
      signup: {
        title: "Создать аккаунт",
        subtitle: "Начните с одного понятного шага.",
        emailLabel: "Email",
        passwordLabel: "Пароль",
        emailPlaceholder: "you@company.com",
        passwordPlaceholder: "••••••••",
        submit: "Создать аккаунт",
        submitting: "Создание аккаунта...",
        hasAccount: "Уже есть аккаунт?",
        login: "Войти",
        terms: "Регистрируясь, вы соглашаетесь с условиями.",
        successTitle: "Проверьте почту",
        successIntro: "Мы отправили ссылку подтверждения на",
        successAction: "Назад ко входу",
      },
      forgotPassword: {
        title: "Сброс пароля",
        subtitle: "Введите email. Если он существует, вы получите ссылку.",
        emailLabel: "Email",
        emailPlaceholder: "you@company.com",
        submit: "Отправить письмо для сброса",
        submitting: "Отправка...",
        successTitle: "Проверьте почту",
        successMessage: "Если email существует, вы получите ссылку.",
        backToLogin: "Назад ко входу",
        invalidEmail: "Введите корректный email.",
        genericError: "Не удалось отправить письмо. Попробуйте снова.",
      },
      resetPassword: {
        title: "Новый пароль",
        subtitle: "Придумайте новый пароль для аккаунта.",
        passwordLabel: "Новый пароль",
        confirmPasswordLabel: "Повторите новый пароль",
        passwordPlaceholder: "Минимум 8 символов",
        confirmPasswordPlaceholder: "Повторите новый пароль",
        submit: "Сохранить новый пароль",
        submitting: "Сохранение...",
        minLengthError: "Используйте минимум 8 символов.",
        mismatchError: "Пароли не совпадают.",
        genericError: "Не удалось обновить пароль. Попробуйте снова.",
        missingSessionTitle: "Откройте ссылку из письма",
        missingSessionMessage: "Для безопасности эта страница работает только из письма.",
        successTitle: "Пароль обновлен",
        successMessage: "Ваш пароль изменен.",
        successAction: "Перейти ко входу",
        backToLogin: "Назад ко входу",
        checkingSession: "Проверяем ссылку...",
      },
    },
    coursesPage: {
      title: "Доступные решения",
      subtitle:
        "Выберите один блокер и уберите его. Начните с того, что тормозит вас сейчас.",
      empty: "Пока нет курсов. Проверьте позже.",
    },
    courseCard: {
      free: "БЕСПЛАТНО",
      pro: "ПЛАТНО",
      openCourse: "Открыть решение",
    },
    courseDetail: {
      back: "Назад к курсам",
      curriculum: "Шаги",
      noLessons: "Пока нет доступных шагов.",
      info: "Информация о курсе",
      lessons: "Уроки",
      access: "Доступ",
      free: "Бесплатно",
      premium: "Премиум",
      start: "Начать",
      soon: "Скоро",
    },
    dashboard: {
      title: "Ваш прогресс",
      welcome: "С возвращением",
      completion: "Выполнение",
      lessons: "Уроки",
      objectiveLabel: "Текущий фокус",
      objectiveTitle: "Продолжайте движение",
      objectiveStart: "Вы завершили",
      objectiveEnd: "уроков. Идите к следующему шагу.",
      continue: "Продолжить",
      streakTitle: "Серия дней",
      streakText: "Вы стабильно двигаетесь вперед.",
      recommended: "Рекомендуем дальше",
      viewAll: "Смотреть все",
    },
    lessonPage: {
      back: "Назад к курсам",
      label: "Урок",
    },
    lessonContent: {
      markStep: "Продолжить",
      contentSoon: "Контент скоро появится.",
      previousLesson: "Предыдущий урок",
      takeQuiz: "Короткая проверка",
      saving: "Сохранение...",
      completed: "Завершено",
      completeLesson: "Завершить урок",
      rewardTitle: "Урок завершен",
      rewardDescription: "Следующий шаг готов.",
      rewardButton: "Продолжить",
      stepLabels: {
        watch: "Смотреть",
        try: "Пробовать",
        think: "Подумать",
        quickInsight: "Короткая идея",
        quickCheck: "Короткая проверка",
        example: "Пример",
        deepDive: "Подробнее",
        teacherQuestion: "Вопрос",
      },
      stepOf: "Шаг {current} из {total}",
      justGettingStarted: "0% · только начали.",
      scrollToContinue: "Прокрутите, чтобы продолжить",
      keepScrollingToUnlock: "Прокрутите дальше, чтобы открыть следующую часть",
      phaseAnchors: {
        default: "Давайте продолжим",
        apply: "Теперь применим это",
        pause: "Сделайте паузу",
        practical: "Теперь к практике",
        guess: "Перед объяснением попробуйте угадать",
      },
      almostThere: "Почти готово.",
      oneMoreBeforeQuickCheck: "Еще один шаг до короткой проверки.",
      quickCheckNext: "Дальше короткая проверка.",
      quickCheckIntro: "Проверим, стало ли понятнее.",
      teacherQuestionPrompt: "Как думаете?",
      deepDiveClosed: "Хотите подробнее?",
      deepDiveOpen: "Скрыть детали",
      nextUp: "Дальше",
    },
    quizPage: {
      subtitle: "Короткая проверка.",
    },
    quizClient: {
      completedTitle: "Квиз завершен",
      scoredPrefix: "Ваш результат",
      backToCourses: "Назад к курсам",
      retake: "Пройти снова",
      rewardTitle: "Отличный результат",
      rewardDescription: "Вы движетесь в правильном направлении.",
      rewardButton: "Продолжить",
      insight: "Ключевая идея",
      lockIn: "Проверить ответ",
      next: "Следующий вопрос",
      reveal: "Завершить",
    },
  },
  uk: {
    auth: {
      login: {
        title: "З поверненням",
        subtitle: "Введіть дані, щоб продовжити.",
        emailLabel: "Email",
        passwordLabel: "Пароль",
        emailPlaceholder: "you@company.com",
        passwordPlaceholder: "••••••••",
        submit: "Увійти",
        submitting: "Вхід...",
        noAccount: "Немає акаунта?",
        createAccount: "Створити акаунт",
        forgotPassword: "Забули пароль?",
        loadingForm: "Завантаження форми...",
      },
      signup: {
        title: "Створити акаунт",
        subtitle: "Почніть з одного чіткого кроку.",
        emailLabel: "Email",
        passwordLabel: "Пароль",
        emailPlaceholder: "you@company.com",
        passwordPlaceholder: "••••••••",
        submit: "Створити акаунт",
        submitting: "Створення акаунта...",
        hasAccount: "Вже маєте акаунт?",
        login: "Увійти",
        terms: "Реєструючись, ви погоджуєтесь з умовами.",
        successTitle: "Перевірте пошту",
        successIntro: "Ми надіслали лист підтвердження на",
        successAction: "Назад до входу",
      },
      forgotPassword: {
        title: "Скидання пароля",
        subtitle: "Введіть email. Якщо він існує, ви отримаєте посилання.",
        emailLabel: "Email",
        emailPlaceholder: "you@company.com",
        submit: "Надіслати лист для скидання",
        submitting: "Надсилання...",
        successTitle: "Перевірте пошту",
        successMessage: "Якщо email існує, ви отримаєте посилання.",
        backToLogin: "Назад до входу",
        invalidEmail: "Введіть коректний email.",
        genericError: "Не вдалося надіслати лист. Спробуйте ще раз.",
      },
      resetPassword: {
        title: "Новий пароль",
        subtitle: "Встановіть новий пароль для акаунта.",
        passwordLabel: "Новий пароль",
        confirmPasswordLabel: "Підтвердьте новий пароль",
        passwordPlaceholder: "Мінімум 8 символів",
        confirmPasswordPlaceholder: "Повторіть новий пароль",
        submit: "Зберегти новий пароль",
        submitting: "Збереження...",
        minLengthError: "Використайте щонайменше 8 символів.",
        mismatchError: "Паролі не збігаються.",
        genericError: "Не вдалося оновити пароль. Спробуйте ще раз.",
        missingSessionTitle: "Відкрийте посилання з листа",
        missingSessionMessage: "Для безпеки ця сторінка працює лише з посилання з листа.",
        successTitle: "Пароль оновлено",
        successMessage: "Ваш пароль змінено.",
        successAction: "Перейти до входу",
        backToLogin: "Назад до входу",
        checkingSession: "Перевіряємо посилання...",
      },
    },
    coursesPage: {
      title: "Доступні рішення",
      subtitle:
        "Оберіть один блокер і приберіть його. Почніть з того, що гальмує вас зараз.",
      empty: "Поки що немає курсів. Перевірте пізніше.",
    },
    courseCard: {
      free: "БЕЗКОШТОВНО",
      pro: "ПЛАТНО",
      openCourse: "Відкрити рішення",
    },
    courseDetail: {
      back: "Назад до курсів",
      curriculum: "Кроки",
      noLessons: "Поки немає доступних кроків.",
      info: "Інформація про курс",
      lessons: "Уроки",
      access: "Доступ",
      free: "Безкоштовно",
      premium: "Преміум",
      start: "Почати",
      soon: "Скоро",
    },
    dashboard: {
      title: "Ваш прогрес",
      welcome: "З поверненням",
      completion: "Виконання",
      lessons: "Уроки",
      objectiveLabel: "Поточний фокус",
      objectiveTitle: "Рухайтесь далі",
      objectiveStart: "Ви завершили",
      objectiveEnd: "уроків. Переходьте до наступного кроку.",
      continue: "Продовжити",
      streakTitle: "Серія днів",
      streakText: "Ви стабільно рухаєтесь вперед.",
      recommended: "Рекомендовано далі",
      viewAll: "Дивитись усі",
    },
    lessonPage: {
      back: "Назад до курсів",
      label: "Урок",
    },
    lessonContent: {
      markStep: "Продовжити",
      contentSoon: "Контент скоро з'явиться.",
      previousLesson: "Попередній урок",
      takeQuiz: "Коротка перевірка",
      saving: "Збереження...",
      completed: "Завершено",
      completeLesson: "Завершити урок",
      rewardTitle: "Урок завершено",
      rewardDescription: "Наступний крок готовий.",
      rewardButton: "Продовжити",
      stepLabels: {
        watch: "Дивитись",
        try: "Спробувати",
        think: "Подумати",
        quickInsight: "Коротка ідея",
        quickCheck: "Коротка перевірка",
        example: "Приклад",
        deepDive: "Детальніше",
        teacherQuestion: "Питання",
      },
      stepOf: "Крок {current} з {total}",
      justGettingStarted: "0% · тільки починаємо.",
      scrollToContinue: "Прокрутіть, щоб продовжити",
      keepScrollingToUnlock: "Прокрутіть далі, щоб відкрити наступну частину",
      phaseAnchors: {
        default: "Продовжимо",
        apply: "Тепер застосуємо це",
        pause: "Зробіть паузу",
        practical: "Тепер до практики",
        guess: "Перед поясненням спробуйте вгадати",
      },
      almostThere: "Майже готово.",
      oneMoreBeforeQuickCheck: "Ще один крок до короткої перевірки.",
      quickCheckNext: "Далі коротка перевірка.",
      quickCheckIntro: "Перевіримо, чи стало зрозуміло.",
      teacherQuestionPrompt: "Як думаєте?",
      deepDiveClosed: "Хочете глибше?",
      deepDiveOpen: "Сховати деталі",
      nextUp: "Далі",
    },
    quizPage: {
      subtitle: "Коротка перевірка.",
    },
    quizClient: {
      completedTitle: "Квіз завершено",
      scoredPrefix: "Ваш результат",
      backToCourses: "Назад до курсів",
      retake: "Пройти знову",
      rewardTitle: "Чудовий результат",
      rewardDescription: "Ви рухаєтесь у правильному напрямку.",
      rewardButton: "Продовжити",
      insight: "Ключова ідея",
      lockIn: "Перевірити відповідь",
      next: "Наступне питання",
      reveal: "Завершити",
    },
  },
  de: {
    auth: {
      login: {
        title: "Willkommen zurück",
        subtitle: "Gib deine Daten ein, um weiterzumachen.",
        emailLabel: "E-Mail",
        passwordLabel: "Passwort",
        emailPlaceholder: "du@firma.com",
        passwordPlaceholder: "••••••••",
        submit: "Anmelden",
        submitting: "Anmeldung...",
        noAccount: "Noch kein Konto?",
        createAccount: "Konto erstellen",
        forgotPassword: "Passwort vergessen?",
        loadingForm: "Formular wird geladen...",
      },
      signup: {
        title: "Konto erstellen",
        subtitle: "Starte mit einem klaren nächsten Schritt.",
        emailLabel: "E-Mail",
        passwordLabel: "Passwort",
        emailPlaceholder: "du@firma.com",
        passwordPlaceholder: "••••••••",
        submit: "Konto erstellen",
        submitting: "Konto wird erstellt...",
        hasAccount: "Hast du schon ein Konto?",
        login: "Anmelden",
        terms: "Mit der Registrierung stimmst du den Bedingungen zu.",
        successTitle: "Prüfe deine E-Mail",
        successIntro: "Wir haben einen Bestätigungslink gesendet an",
        successAction: "Zurück zum Login",
      },
      forgotPassword: {
        title: "Passwort zuruecksetzen",
        subtitle: "Gib deine E-Mail ein. Wenn sie existiert, bekommst du einen Link.",
        emailLabel: "E-Mail",
        emailPlaceholder: "du@firma.com",
        submit: "Reset-E-Mail senden",
        submitting: "Senden...",
        successTitle: "Pruefe deine E-Mail",
        successMessage: "Wenn die E-Mail existiert, bekommst du einen Link.",
        backToLogin: "Zurueck zum Login",
        invalidEmail: "Gib eine gueltige E-Mail ein.",
        genericError: "Reset-E-Mail konnte nicht gesendet werden. Versuch es erneut.",
      },
      resetPassword: {
        title: "Neues Passwort setzen",
        subtitle: "Waehle ein neues Passwort fuer dein Konto.",
        passwordLabel: "Neues Passwort",
        confirmPasswordLabel: "Neues Passwort bestaetigen",
        passwordPlaceholder: "Mindestens 8 Zeichen",
        confirmPasswordPlaceholder: "Neues Passwort wiederholen",
        submit: "Neues Passwort setzen",
        submitting: "Speichern...",
        minLengthError: "Nutze mindestens 8 Zeichen.",
        mismatchError: "Passwoerter stimmen nicht ueberein.",
        genericError: "Passwort konnte nicht aktualisiert werden. Versuch es erneut.",
        missingSessionTitle: "Oeffne den Link aus deiner E-Mail",
        missingSessionMessage: "Aus Sicherheitsgruenden funktioniert diese Seite nur mit diesem Link.",
        successTitle: "Passwort aktualisiert",
        successMessage: "Dein Passwort wurde geaendert.",
        successAction: "Zum Login",
        backToLogin: "Zurueck zum Login",
        checkingSession: "Link wird geprueft...",
      },
    },
    coursesPage: {
      title: "Verfügbare Lösungen",
      subtitle:
        "Wähle einen Blocker und entferne ihn. Starte mit dem, was dich jetzt bremst.",
      empty: "Noch keine Kurse verfügbar. Schau später wieder vorbei.",
    },
    courseCard: {
      free: "KOSTENLOS",
      pro: "BEZAHLT",
      openCourse: "Lösung öffnen",
    },
    courseDetail: {
      back: "Zurück zu den Kursen",
      curriculum: "Schritte",
      noLessons: "Noch keine Schritte verfügbar.",
      info: "Kursinfo",
      lessons: "Lektionen",
      access: "Zugang",
      free: "Kostenlos",
      premium: "Premium",
      start: "Jetzt starten",
      soon: "Bald verfügbar",
    },
    dashboard: {
      title: "Dein Fortschritt",
      welcome: "Willkommen zurück",
      completion: "Fortschritt",
      lessons: "Lektionen",
      objectiveLabel: "Aktueller Fokus",
      objectiveTitle: "Bleib dran",
      objectiveStart: "Du hast",
      objectiveEnd: "Lektionen abgeschlossen. Mach mit dem nächsten Schritt weiter.",
      continue: "Weiter",
      streakTitle: "Tagesserie",
      streakText: "Du kommst konstant voran.",
      recommended: "Empfohlen als Nächstes",
      viewAll: "Alle ansehen",
    },
    lessonPage: {
      back: "Zurück zu den Kursen",
      label: "Lektion",
    },
    lessonContent: {
      markStep: "Weiter",
      contentSoon: "Inhalt kommt bald.",
      previousLesson: "Vorherige Lektion",
      takeQuiz: "Schneller Check",
      saving: "Speichern...",
      completed: "Abgeschlossen",
      completeLesson: "Lektion beenden",
      rewardTitle: "Lektion abgeschlossen",
      rewardDescription: "Der nächste Schritt ist bereit.",
      rewardButton: "Weiter",
      stepLabels: {
        watch: "Ansehen",
        try: "Ausprobieren",
        think: "Nachdenken",
        quickInsight: "Kurzer Hinweis",
        quickCheck: "Schneller Check",
        example: "Beispiel",
        deepDive: "Mehr dazu",
        teacherQuestion: "Frage",
      },
      stepOf: "Schritt {current} von {total}",
      justGettingStarted: "0 % · gerade erst gestartet",
      scrollToContinue: "Scrollen, um weiterzumachen",
      keepScrollingToUnlock: "Weiter scrollen, um den nächsten Teil freizuschalten",
      phaseAnchors: {
        default: "Weiter geht's",
        apply: "Jetzt setzen wir das um",
        pause: "Mach kurz Pause",
        practical: "Jetzt wird es praktisch",
        guess: "Bevor ich erklare: rate mal",
      },
      almostThere: "Fast geschafft.",
      oneMoreBeforeQuickCheck: "Noch ein Schritt bis zum schnellen Check.",
      quickCheckNext: "Als Nächstes: schneller Check.",
      quickCheckIntro: "Schauen wir, ob das klar ist.",
      teacherQuestionPrompt: "Was denkst du?",
      deepDiveClosed: "Willst du tiefer einsteigen?",
      deepDiveOpen: "Details ausblenden",
      nextUp: "Als Nächstes",
    },
    quizPage: {
      subtitle: "Schneller Check.",
    },
    quizClient: {
      completedTitle: "Quiz abgeschlossen",
      scoredPrefix: "Dein Ergebnis",
      backToCourses: "Zurück zu Kursen",
      retake: "Wiederholen",
      rewardTitle: "Starkes Ergebnis",
      rewardDescription: "Du kommst gut voran.",
      rewardButton: "Weiter",
      insight: "Kernaussage",
      lockIn: "Antwort prüfen",
      next: "Nächste Frage",
      reveal: "Fertig",
    },
  },
  es: {
    auth: {
      login: {
        title: "Bienvenido de nuevo",
        subtitle: "Ingresa tus datos para continuar.",
        emailLabel: "Correo",
        passwordLabel: "Contrasena",
        emailPlaceholder: "tu@empresa.com",
        passwordPlaceholder: "••••••••",
        submit: "Entrar",
        submitting: "Entrando...",
        noAccount: "No tienes cuenta?",
        createAccount: "Crear cuenta",
        forgotPassword: "Olvidaste tu contrasena?",
        loadingForm: "Cargando formulario...",
      },
      signup: {
        title: "Crear cuenta",
        subtitle: "Empieza con un siguiente paso claro.",
        emailLabel: "Correo",
        passwordLabel: "Contrasena",
        emailPlaceholder: "tu@empresa.com",
        passwordPlaceholder: "••••••••",
        submit: "Crear cuenta",
        submitting: "Creando cuenta...",
        hasAccount: "Ya tienes cuenta?",
        login: "Entrar",
        terms: "Al registrarte aceptas los terminos.",
        successTitle: "Revisa tu correo",
        successIntro: "Enviamos un enlace de confirmacion a",
        successAction: "Volver a entrar",
      },
      forgotPassword: {
        title: "Restablecer contrasena",
        subtitle: "Ingresa tu correo. Si existe, recibiras un enlace.",
        emailLabel: "Correo",
        emailPlaceholder: "tu@empresa.com",
        submit: "Enviar email de restablecimiento",
        submitting: "Enviando...",
        successTitle: "Revisa tu correo",
        successMessage: "Si el correo existe, recibiras un enlace.",
        backToLogin: "Volver a entrar",
        invalidEmail: "Ingresa un correo valido.",
        genericError: "No se pudo enviar el email. Intentalo de nuevo.",
      },
      resetPassword: {
        title: "Definir nueva contrasena",
        subtitle: "Elige una nueva contrasena para tu cuenta.",
        passwordLabel: "Nueva contrasena",
        confirmPasswordLabel: "Confirmar nueva contrasena",
        passwordPlaceholder: "Minimo 8 caracteres",
        confirmPasswordPlaceholder: "Repite la nueva contrasena",
        submit: "Guardar nueva contrasena",
        submitting: "Guardando...",
        minLengthError: "Usa al menos 8 caracteres.",
        mismatchError: "Las contrasenas no coinciden.",
        genericError: "No se pudo actualizar la contrasena. Intentalo de nuevo.",
        missingSessionTitle: "Abre el enlace del email",
        missingSessionMessage: "Por seguridad, esta pagina solo funciona desde ese enlace.",
        successTitle: "Contrasena actualizada",
        successMessage: "Tu contrasena fue cambiada.",
        successAction: "Ir al login",
        backToLogin: "Volver a entrar",
        checkingSession: "Comprobando enlace...",
      },
    },
    coursesPage: {
      title: "Soluciones disponibles",
      subtitle:
        "Elige un bloqueo y elimínalo. Empieza por lo que te frena ahora.",
      empty: "Todavía no hay cursos. Vuelve pronto.",
    },
    courseCard: {
      free: "GRATIS",
      pro: "DE PAGO",
      openCourse: "Abrir solucion",
    },
    courseDetail: {
      back: "Volver a cursos",
      curriculum: "Pasos",
      noLessons: "Todavía no hay pasos disponibles.",
      info: "Info del curso",
      lessons: "Lecciones",
      access: "Acceso",
      free: "Gratis",
      premium: "Premium",
      start: "Empezar",
      soon: "Proximamente",
    },
    dashboard: {
      title: "Tu progreso",
      welcome: "Bienvenido de nuevo",
      completion: "Progreso",
      lessons: "Lecciones",
      objectiveLabel: "Enfoque actual",
      objectiveTitle: "Sigue avanzando",
      objectiveStart: "Has completado",
      objectiveEnd: "lecciones. Sigue con el siguiente paso.",
      continue: "Continuar",
      streakTitle: "Racha de dias",
      streakText: "Estas avanzando con constancia.",
      recommended: "Siguiente recomendado",
      viewAll: "Ver todo",
    },
    lessonPage: {
      back: "Volver a cursos",
      label: "Leccion",
    },
    lessonContent: {
      markStep: "Continuar",
      contentSoon: "Contenido disponible pronto.",
      previousLesson: "Leccion anterior",
      takeQuiz: "Comprobacion rapida",
      saving: "Guardando...",
      completed: "Completado",
      completeLesson: "Terminar leccion",
      rewardTitle: "Leccion completada",
      rewardDescription: "El siguiente paso esta listo.",
      rewardButton: "Continuar",
      stepLabels: {
        watch: "Ver",
        try: "Probar",
        think: "Pensar",
        quickInsight: "Idea rapida",
        quickCheck: "Comprobacion rapida",
        example: "Ejemplo",
        deepDive: "Mas a fondo",
        teacherQuestion: "Pregunta",
      },
      stepOf: "Paso {current} de {total}",
      justGettingStarted: "0% · recien empezando",
      scrollToContinue: "Desplaza para continuar",
      keepScrollingToUnlock: "Sigue desplazando para desbloquear la siguiente parte",
      phaseAnchors: {
        default: "Sigamos",
        apply: "Ahora vamos a aplicarlo",
        pause: "Haz una pausa",
        practical: "Aqui se vuelve practico",
        guess: "Antes de explicar, adivina",
      },
      almostThere: "Casi listo.",
      oneMoreBeforeQuickCheck: "Un paso mas antes de una comprobacion rapida.",
      quickCheckNext: "Sigue una comprobacion rapida.",
      quickCheckIntro: "Veamos si esto quedo claro.",
      teacherQuestionPrompt: "Que piensas?",
      deepDiveClosed: "Quieres ir mas a fondo?",
      deepDiveOpen: "Ocultar detalles",
      nextUp: "Siguiente",
    },
    quizPage: {
      subtitle: "Comprobacion rapida.",
    },
    quizClient: {
      completedTitle: "Quiz completado",
      scoredPrefix: "Tu resultado",
      backToCourses: "Volver a cursos",
      retake: "Repetir",
      rewardTitle: "Muy buen resultado",
      rewardDescription: "Estas avanzando bien.",
      rewardButton: "Continuar",
      insight: "Idea clave",
      lockIn: "Comprobar respuesta",
      next: "Siguiente pregunta",
      reveal: "Terminar",
    },
  },
  it: {
    auth: {
      login: {
        title: "Bentornato",
        subtitle: "Inserisci i tuoi dati per continuare.",
        emailLabel: "Email",
        passwordLabel: "Password",
        emailPlaceholder: "tu@azienda.com",
        passwordPlaceholder: "••••••••",
        submit: "Accedi",
        submitting: "Accesso...",
        noAccount: "Non hai un account?",
        createAccount: "Crea account",
        forgotPassword: "Password dimenticata?",
        loadingForm: "Caricamento modulo...",
      },
      signup: {
        title: "Crea account",
        subtitle: "Inizia con un prossimo passo chiaro.",
        emailLabel: "Email",
        passwordLabel: "Password",
        emailPlaceholder: "tu@azienda.com",
        passwordPlaceholder: "••••••••",
        submit: "Crea account",
        submitting: "Creazione account...",
        hasAccount: "Hai gia un account?",
        login: "Accedi",
        terms: "Registrandoti accetti i termini.",
        successTitle: "Controlla la tua email",
        successIntro: "Abbiamo inviato un link di conferma a",
        successAction: "Torna al login",
      },
      forgotPassword: {
        title: "Reimposta password",
        subtitle: "Inserisci la tua email. Se esiste, riceverai un link.",
        emailLabel: "Email",
        emailPlaceholder: "tu@azienda.com",
        submit: "Invia email di reimpostazione",
        submitting: "Invio...",
        successTitle: "Controlla la tua email",
        successMessage: "Se l'email esiste, riceverai un link.",
        backToLogin: "Torna al login",
        invalidEmail: "Inserisci una email valida.",
        genericError: "Impossibile inviare l'email. Riprova.",
      },
      resetPassword: {
        title: "Imposta nuova password",
        subtitle: "Scegli una nuova password per il tuo account.",
        passwordLabel: "Nuova password",
        confirmPasswordLabel: "Conferma nuova password",
        passwordPlaceholder: "Almeno 8 caratteri",
        confirmPasswordPlaceholder: "Ripeti la nuova password",
        submit: "Imposta nuova password",
        submitting: "Salvataggio...",
        minLengthError: "Usa almeno 8 caratteri.",
        mismatchError: "Le password non coincidono.",
        genericError: "Impossibile aggiornare la password. Riprova.",
        missingSessionTitle: "Apri il link ricevuto via email",
        missingSessionMessage: "Per sicurezza, questa pagina funziona solo con quel link.",
        successTitle: "Password aggiornata",
        successMessage: "La tua password e stata cambiata.",
        successAction: "Vai al login",
        backToLogin: "Torna al login",
        checkingSession: "Verifica del link...",
      },
    },
    coursesPage: {
      title: "Soluzioni disponibili",
      subtitle:
        "Scegli un blocco e rimuovilo. Parti da quello che ti rallenta adesso.",
      empty: "Nessun corso disponibile per ora. Torna presto.",
    },
    courseCard: {
      free: "GRATIS",
      pro: "A PAGAMENTO",
      openCourse: "Apri soluzione",
    },
    courseDetail: {
      back: "Torna ai corsi",
      curriculum: "Passi",
      noLessons: "Nessun passo disponibile al momento.",
      info: "Info corso",
      lessons: "Lezioni",
      access: "Accesso",
      free: "Gratis",
      premium: "Premium",
      start: "Inizia ora",
      soon: "In arrivo",
    },
    dashboard: {
      title: "Il tuo progresso",
      welcome: "Bentornato",
      completion: "Completamento",
      lessons: "Lezioni",
      objectiveLabel: "Focus attuale",
      objectiveTitle: "Continua ad avanzare",
      objectiveStart: "Hai completato",
      objectiveEnd: "lezioni. Continua con il prossimo passo.",
      continue: "Continua",
      streakTitle: "Serie di giorni",
      streakText: "Stai avanzando con costanza.",
      recommended: "Prossimo consigliato",
      viewAll: "Vedi tutto",
    },
    lessonPage: {
      back: "Torna ai corsi",
      label: "Lezione",
    },
    lessonContent: {
      markStep: "Continua",
      contentSoon: "Contenuto disponibile presto.",
      previousLesson: "Lezione precedente",
      takeQuiz: "Verifica rapida",
      saving: "Salvataggio...",
      completed: "Completato",
      completeLesson: "Termina lezione",
      rewardTitle: "Lezione completata",
      rewardDescription: "Il prossimo passo e pronto.",
      rewardButton: "Continua",
      stepLabels: {
        watch: "Guarda",
        try: "Prova",
        think: "Pensa",
        quickInsight: "Idea rapida",
        quickCheck: "Verifica rapida",
        example: "Esempio",
        deepDive: "Approfondisci",
        teacherQuestion: "Domanda",
      },
      stepOf: "Passo {current} di {total}",
      justGettingStarted: "0% · appena iniziato",
      scrollToContinue: "Scorri per continuare",
      keepScrollingToUnlock: "Continua a scorrere per sbloccare la prossima parte",
      phaseAnchors: {
        default: "Andiamo avanti",
        apply: "Ora applichiamolo",
        pause: "Fai una pausa",
        practical: "Qui diventa pratico",
        guess: "Prima di spiegare, prova a indovinare",
      },
      almostThere: "Quasi fatto.",
      oneMoreBeforeQuickCheck: "Un altro passo prima della verifica rapida.",
      quickCheckNext: "Ora una verifica rapida.",
      quickCheckIntro: "Vediamo se e chiaro.",
      teacherQuestionPrompt: "Che ne pensi?",
      deepDiveClosed: "Vuoi approfondire?",
      deepDiveOpen: "Nascondi dettagli",
      nextUp: "Prossimo",
    },
    quizPage: {
      subtitle: "Verifica rapida.",
    },
    quizClient: {
      completedTitle: "Quiz completato",
      scoredPrefix: "Hai ottenuto",
      backToCourses: "Torna ai corsi",
      retake: "Riprova",
      rewardTitle: "Ottimo risultato",
      rewardDescription: "Stai andando nella direzione giusta.",
      rewardButton: "Continua",
      insight: "Idea chiave",
      lockIn: "Verifica risposta",
      next: "Domanda successiva",
      reveal: "Termina",
    },
  },
  pt: {
    auth: {
      login: {
        title: "Bem-vindo de volta",
        subtitle: "Insira seus dados para continuar.",
        emailLabel: "Email",
        passwordLabel: "Senha",
        emailPlaceholder: "voce@empresa.com",
        passwordPlaceholder: "••••••••",
        submit: "Entrar",
        submitting: "Entrando...",
        noAccount: "Nao tem uma conta?",
        createAccount: "Criar conta",
        forgotPassword: "Esqueceu a senha?",
        loadingForm: "Carregando formulario...",
      },
      signup: {
        title: "Crie sua conta",
        subtitle: "Comece com um proximo passo claro.",
        emailLabel: "Email",
        passwordLabel: "Senha",
        emailPlaceholder: "voce@empresa.com",
        passwordPlaceholder: "••••••••",
        submit: "Criar conta",
        submitting: "Criando conta...",
        hasAccount: "Ja tem uma conta?",
        login: "Entrar",
        terms: "Ao se cadastrar, voce aceita nossos termos.",
        successTitle: "Verifique seu email",
        successIntro: "Enviamos um link de confirmacao para",
        successAction: "Voltar ao login",
      },
      forgotPassword: {
        title: "Redefinir senha",
        subtitle: "Insira seu email. Se existir, voce recebera um link.",
        emailLabel: "Email",
        emailPlaceholder: "voce@empresa.com",
        submit: "Enviar email de redefinicao",
        submitting: "Enviando...",
        successTitle: "Verifique seu email",
        successMessage: "Se o email existir, voce recebera um link.",
        backToLogin: "Voltar ao login",
        invalidEmail: "Insira um email valido.",
        genericError: "Nao foi possivel enviar o email. Tente novamente.",
      },
      resetPassword: {
        title: "Definir nova senha",
        subtitle: "Escolha uma nova senha para sua conta.",
        passwordLabel: "Nova senha",
        confirmPasswordLabel: "Confirme a nova senha",
        passwordPlaceholder: "No minimo 8 caracteres",
        confirmPasswordPlaceholder: "Repita a nova senha",
        submit: "Definir nova senha",
        submitting: "Salvando...",
        minLengthError: "Use pelo menos 8 caracteres.",
        mismatchError: "As senhas nao coincidem.",
        genericError: "Nao foi possivel atualizar a senha. Tente novamente.",
        missingSessionTitle: "Abra o link recebido por email",
        missingSessionMessage: "Por seguranca, esta pagina funciona apenas a partir do link do email.",
        successTitle: "Senha atualizada",
        successMessage: "Sua senha foi alterada.",
        successAction: "Ir para o login",
        backToLogin: "Voltar ao login",
        checkingSession: "Verificando o link...",
      },
    },
    coursesPage: {
      title: "Solucoes disponiveis",
      subtitle:
        "Escolha um bloqueio e remova-o. Comece pelo que esta te atrasando agora.",
      empty: "Nenhum curso disponivel ainda. Volte em breve.",
    },
    courseCard: {
      free: "GRATIS",
      pro: "PAGO",
      openCourse: "Abrir solucao",
    },
    courseDetail: {
      back: "Voltar aos cursos",
      curriculum: "Etapas",
      noLessons: "Nenhuma etapa disponivel ainda.",
      info: "Info do curso",
      lessons: "Aulas",
      access: "Acesso",
      free: "Gratis",
      premium: "Premium",
      start: "Comecar agora",
      soon: "Em breve",
    },
    dashboard: {
      title: "Seu progresso",
      welcome: "Bem-vindo de volta",
      completion: "Conclusao",
      lessons: "Aulas",
      objectiveLabel: "Foco atual",
      objectiveTitle: "Continue avancando",
      objectiveStart: "Voce concluiu",
      objectiveEnd: "aulas. Continue com o proximo passo.",
      continue: "Continuar",
      streakTitle: "Sequencia de dias",
      streakText: "Voce esta avancando com consistencia.",
      recommended: "Proximo recomendado",
      viewAll: "Ver tudo",
    },
    lessonPage: {
      back: "Voltar aos cursos",
      label: "Aula",
    },
    lessonContent: {
      markStep: "Continuar",
      contentSoon: "Conteudo disponivel em breve.",
      previousLesson: "Aula anterior",
      takeQuiz: "Verificacao rapida",
      saving: "Salvando...",
      completed: "Concluido",
      completeLesson: "Finalizar aula",
      rewardTitle: "Aula concluida",
      rewardDescription: "O proximo passo esta pronto.",
      rewardButton: "Continuar",
      stepLabels: {
        watch: "Assistir",
        try: "Tentar",
        think: "Pensar",
        quickInsight: "Dica rapida",
        quickCheck: "Verificacao rapida",
        example: "Exemplo",
        deepDive: "Aprofundar",
        teacherQuestion: "Pergunta",
      },
      stepOf: "Passo {current} de {total}",
      justGettingStarted: "0% · apenas comecando",
      scrollToContinue: "Role para continuar",
      keepScrollingToUnlock: "Continue rolando para desbloquear a proxima parte",
      phaseAnchors: {
        default: "Vamos continuar",
        apply: "Agora vamos aplicar isso",
        pause: "Faca uma pausa",
        practical: "Aqui fica pratico",
        guess: "Antes de explicar, tente adivinhar",
      },
      almostThere: "Quase la.",
      oneMoreBeforeQuickCheck: "Mais um passo antes da verificacao rapida.",
      quickCheckNext: "A seguir, verificacao rapida.",
      quickCheckIntro: "Vamos ver se ficou claro.",
      teacherQuestionPrompt: "O que voce acha?",
      deepDiveClosed: "Quer se aprofundar?",
      deepDiveOpen: "Ocultar detalhes",
      nextUp: "A seguir",
    },
    quizPage: {
      subtitle: "Verificacao rapida.",
    },
    quizClient: {
      completedTitle: "Quiz concluido",
      scoredPrefix: "Voce acertou",
      backToCourses: "Voltar aos cursos",
      retake: "Refazer",
      rewardTitle: "Otimo resultado",
      rewardDescription: "Voce esta avancando bem.",
      rewardButton: "Continuar",
      insight: "Ideia-chave",
      lockIn: "Verificar resposta",
      next: "Proxima pergunta",
      reveal: "Finalizar",
    },
  },
});

export function getAppCopy(lang: Locale): AppCopy {
  return appCopyByLocale[lang];
}
